import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useRouter } from 'expo-router';
import { ArrowLeft } from 'lucide-react-native';
import { useEffect } from 'react';
import { Image, Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { PremiumFooter } from '../../src/components/landing/PremiumFooter';
import { Container } from '../../src/components/primitives/Container';
import { Text } from '../../src/components/primitives/Text';
import { useTranslation } from '../../src/contexts/TranslationContext';
import { trackEvent } from '../../src/lib/analytics';
import type { BlogFeedResponse, BlogLocale, BlogPost } from '../../src/lib/api';
import { blogAPI } from '../../src/lib/api';
import { useMixpanel } from '../../src/lib/mixpanel';
import { colors } from '../../src/theme/colors';
import { spacing } from '../../src/theme/spacing';

// ---------------------------------------------------------------------------
// Public route: /blog
// Renders the localized feed of published blog posts (channel = flowsell).
// ---------------------------------------------------------------------------

function CategoryBadge({ label }: { label: string }) {
  return (
    <View style={styles.tag}>
      <Text variant="caption" color={colors.primary[500]} style={styles.tagText}>
        {label}
      </Text>
    </View>
  );
}

interface PostCardProps {
  post: BlogPost;
  onPress: () => void;
}

function PostCard({ post, onPress }: PostCardProps) {
  const dateLabel = post.publishedAt ? format(new Date(post.publishedAt), 'MMM d, yyyy') : null;

  return (
    <Pressable
      testID={`blog-post-${post.id}`}
      onPress={onPress}
      style={({ pressed }) => [styles.card, pressed && styles.cardPressed]}
      accessibilityRole="button"
      accessibilityLabel={post.title}
    >
      {post.coverImageUrl ? (
        <Image
          testID={`blog-cover-${post.id}`}
          source={{ uri: post.coverImageUrl }}
          style={styles.cover}
          resizeMode="cover"
          accessibilityIgnoresInvertColors
        />
      ) : null}

      <View style={styles.cardMeta}>
        {dateLabel ? (
          <Text variant="caption" color={colors.text.tertiary}>
            {dateLabel}
          </Text>
        ) : null}
      </View>

      <Text variant="h4" style={styles.cardTitle}>
        {post.title}
      </Text>

      {post.excerpt ? (
        <Text variant="body" color={colors.text.secondary} style={styles.cardExcerpt}>
          {post.excerpt}
        </Text>
      ) : null}

      {post.categories.length > 0 ? (
        <View style={styles.tagsRow}>
          {post.categories.map((category) => (
            <CategoryBadge key={category.key} label={category.name} />
          ))}
        </View>
      ) : null}

      <View style={styles.cardDivider} />
    </Pressable>
  );
}

export default function BlogListPage() {
  const router = useRouter();
  const { t, language } = useTranslation();
  const mp = useMixpanel();
  const locale = language as BlogLocale;

  const { data, isLoading, isError } = useQuery<BlogFeedResponse>({
    queryKey: ['blog-feed', locale],
    queryFn: async () => {
      const response = await blogAPI.getPosts({ locale, page: 1, limit: 20 });
      return response.data;
    },
    // staleTime 0 — public content must reflect a publish immediately. Anything
    // higher serves the persisted feed on revisit without a request, hiding a
    // post that went live since the last visit. The payload is ~1KB and the
    // response revalidates via ETag, so refetching per visit is cheap.
    staleTime: 0,
  });

  useEffect(() => {
    if (data && !isLoading) {
      trackEvent(mp, 'blog.feed_viewed', { post_count: data.data.length, locale });
    }
  }, [data]);

  const posts = data?.data ?? [];

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Container maxWidth="md">
        <Pressable
          onPress={() => router.push('/landing')}
          style={styles.backButton}
          accessibilityRole="button"
          accessibilityLabel={t('blog.backToHome')}
        >
          <ArrowLeft size={20} color={colors.primary[500]} />
          <Text variant="bodySmall" color={colors.primary[500]}>
            {t('blog.backToHome')}
          </Text>
        </Pressable>

        <View style={styles.header}>
          <Text variant="h1" style={styles.title} accessibilityRole="header">
            {t('blog.title')}
          </Text>
          <Text variant="body" color={colors.text.secondary}>
            {t('blog.subtitle')}
          </Text>
        </View>

        <View style={styles.divider} />

        {isLoading ? (
          <View testID="blog-loading" style={styles.stateContainer}>
            <Text variant="body" color={colors.text.secondary}>
              {t('blog.loading')}
            </Text>
          </View>
        ) : isError ? (
          <View testID="blog-error" style={styles.stateContainer}>
            <Text variant="body" color={colors.status.error.main}>
              {t('blog.error')}
            </Text>
          </View>
        ) : posts.length === 0 ? (
          <View testID="blog-empty" style={styles.stateContainer}>
            <Text variant="body" color={colors.text.secondary}>
              {t('blog.empty')}
            </Text>
          </View>
        ) : (
          <View testID="blog-list">
            {posts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                onPress={() => router.push(`/blog/${post.slug}` as any)}
              />
            ))}
          </View>
        )}
      </Container>

      <PremiumFooter />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  content: {
    flexGrow: 1,
    paddingTop: Platform.select({ web: 60, default: 40 }),
    paddingBottom: spacing.xxl,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xl,
    alignSelf: 'flex-start',
  },
  header: {
    marginBottom: spacing.lg,
    gap: spacing.sm,
  },
  title: {
    fontSize: Platform.select({ web: 42, default: 32 }),
    fontWeight: '700',
    letterSpacing: -0.5,
  },
  divider: {
    height: 1,
    backgroundColor: colors.border.muted,
    marginBottom: spacing.xxl,
  },
  stateContainer: {
    paddingVertical: spacing.xxl,
    alignItems: 'center',
  },
  card: {
    paddingVertical: spacing.lg,
    paddingHorizontal: 0,
  },
  cardPressed: {
    opacity: 0.7,
  },
  cover: {
    width: '100%',
    aspectRatio: 16 / 9,
    borderRadius: 8,
    marginBottom: spacing.md,
    backgroundColor: colors.background.secondary,
  },
  cardMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  cardTitle: {
    fontWeight: '700',
    marginBottom: spacing.sm,
    color: colors.text.primary,
  },
  cardExcerpt: {
    marginBottom: spacing.sm,
  },
  tagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  tag: {
    backgroundColor: colors.background.secondary,
    borderRadius: 4,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
  },
  tagText: {
    fontWeight: '500',
  },
  cardDivider: {
    height: 1,
    backgroundColor: colors.border.muted,
    marginTop: spacing.lg,
  },
});
